import React, { FC, useCallback, useMemo } from "react"
import {
    AddButton,
    ExclusiveChoiceDropdown,
    MultipleChoiceDropdown
} from "dash-styled"

import { APIMeasurerMetric } from "../../../../../../../../../../../../Models/API/APIMetric"
import {
    FetchState,
    getValue
} from "../../../../../../../../../../../../Models/Client/FetchState"
import {
    MetricFields,
    MetricField
} from "../../../../../../../../../../../../Models/API/AppMetricModel"
import { RemoveButton } from "../../../../../../../../../../../Common/Button/RemoveButton"

interface MeasurerMetricProps {
    appMeasurerMetrics?: MetricFields[]
    measurerMetrics: FetchState<APIMeasurerMetric[]>
    appParams?: any
    onChange: (value: { measurerMetrics?: MetricFields[] }) => void
}

export const MeasurerMetric: FC<MeasurerMetricProps> = ({
    appMeasurerMetrics = [],
    measurerMetrics,
    appParams,
    onChange
}) => {
    const measurerMetricsLabel = useMemo<string[]>(() => {
        const selectedMetrics = appMeasurerMetrics.flatMap(amm =>
            Object.keys(amm)
        )
        return (
            getValue(measurerMetrics)
                ?.map(({ name }) => name)
                .filter(name => !selectedMetrics.includes(name)) ?? []
        )
    }, [appMeasurerMetrics, measurerMetrics])
    const handleChange = useCallback(
        (value: MetricFields[]) => {
            onChange({
                measurerMetrics: value
            })
        },
        [onChange]
    )
    const handleAdd = useMemo(
        () =>
            Object.keys(appMeasurerMetrics).length >=
            measurerMetricsLabel.length
                ? undefined
                : () =>
                      handleChange(
                          appMeasurerMetrics.concat([
                              { [measurerMetricsLabel[0]]: {} }
                          ])
                      ),
        [appMeasurerMetrics, measurerMetricsLabel, handleChange]
    )

    const extractedAppFields = useMemo<string[]>(
        () =>
            appParams !== undefined
                ? "fields" in appParams && Array.isArray(appParams["fields"])
                    ? appParams["fields"].flatMap(f =>
                          typeof f === "object" && "name" in f
                              ? [f?.name]
                              : typeof f === "string"
                              ? [f]
                              : []
                      )
                    : []
                : [],
        [appParams]
    )

    const extractedAppChoices = useMemo<string[]>(
        () =>
            appParams !== undefined
                ? "choices" in appParams && Array.isArray(appParams["choices"])
                    ? appParams["choices"].flatMap(c =>
                          typeof c === "object" && "value" in c
                              ? [c.value]
                              : typeof c === "string"
                              ? [c]
                              : []
                      )
                    : []
                : [],
        [appParams]
    )

    const renderMetricChoices = useCallback(
        ([metricName, fields]: [
            string,
            { [key in MetricField]?: string[] }
        ]) => {
            const handleAppMeasurerNameChange = (updatedMetricName: string) =>
                handleChange(
                    appMeasurerMetrics?.flatMap(amm =>
                        metricName in amm
                            ? [{ [updatedMetricName]: fields }]
                            : [amm]
                    )
                )

            const handleAppMeasurerFieldsChange = (fieldName: string) => {
                const fieldsKey =
                    extractedAppChoices.length !== 0 ? "choices" : "fields"
                const fieldsValue = fields[fieldsKey] ?? []
                handleChange(
                    appMeasurerMetrics?.flatMap(amm =>
                        metricName in amm
                            ? [
                                  {
                                      [metricName]: {
                                          [fieldsKey]: fieldsValue.includes(
                                              fieldName
                                          )
                                              ? fieldsValue.filter(
                                                    name => name !== fieldName
                                                )
                                              : fieldsValue.concat([fieldName])
                                      }
                                  }
                              ]
                            : [amm]
                    )
                )
            }

            const handleRemove = () =>
                handleChange(
                    appMeasurerMetrics.filter(
                        appMeasurerMetric => !(metricName in appMeasurerMetric)
                    )
                )

            const fieldChoices =
                extractedAppChoices.length !== 0
                    ? extractedAppChoices
                    : extractedAppFields
            return (
                <div className="flex items-center mb-2" key={metricName}>
                    Metric:
                    <ExclusiveChoiceDropdown
                        className="flex-grow mx-4"
                        testId="measurer-metric"
                        choices={measurerMetricsLabel}
                        value={metricName}
                        onClick={handleAppMeasurerNameChange}
                        size="sm"
                    />
                    {fieldChoices.length !== 0 && (
                        <>
                            for:
                            <MultipleChoiceDropdown
                                className="flex-grow ml-2"
                                testId="measurer-metric-fields"
                                choices={fieldChoices}
                                values={Object.values(fields)[0] ?? []}
                                onClick={handleAppMeasurerFieldsChange}
                                size="sm"
                            />
                        </>
                    )}
                    <RemoveButton
                        testId="metric-remove-button"
                        className="text-grape-900"
                        icon="close"
                        onRemove={handleRemove}
                    />
                </div>
            )
        },
        [
            appMeasurerMetrics,
            extractedAppChoices,
            extractedAppFields,
            measurerMetricsLabel,
            handleChange
        ]
    )

    return (
        <>
            {appMeasurerMetrics.map(amm =>
                Object.entries(amm).map(renderMetricChoices)
            )}
            {handleAdd !== undefined && (
                <AddButton
                    className="ml-4 xl:ml-0 mt-2"
                    text="Add metric configuration"
                    testId="add-metric-configuration-button"
                    onClick={handleAdd}
                />
            )}
        </>
    )
}
